import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createFileRoute, useBlocker, useRouter } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Textarea } from '@/components/atoms/textarea';
import { DatePicker } from '@/components/molecules/date-picker';
import { ImpactRow } from '@/components/molecules/ImpactRow';
import { SectionCard } from '@/components/molecules/SectionCard';
import { StatusBadge } from '@/components/molecules/StatusBadge';
import { expenseStatusTone, reimbursementStatusTone } from '@/components/molecules/StatusBadgeTone';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { FormPageLayout } from '@/components/layouts';
import { ExpenseAttachmentsSection } from '@/components/organisms/expenses/ExpenseAttachmentsSection';
import { useApproveExpense } from '@/hooks/expenses/useApproveExpense';
import { useApproveReimbursement } from '@/hooks/expenses/useApproveReimbursement';
import { useExpense } from '@/hooks/expenses/useExpense';
import { useJobProfitability } from '@/hooks/expenses/useJobProfitability';
import { usePayReimbursement } from '@/hooks/expenses/usePayReimbursement';
import { useRejectExpense } from '@/hooks/expenses/useRejectExpense';
import { useSubmitExpense } from '@/hooks/expenses/useSubmitExpense';
import { useUpdateExpense } from '@/hooks/expenses/useUpdateExpense';
import { useJobAppointments } from '@/hooks/jobs/useJobAppointments';
import { useJob } from '@/hooks/jobs/useJob';
import { useUsers } from '@/hooks/settings/useUsers';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { useScheduleTimezone } from '@/lib/time/scheduleTimezone';
import type { Expense, ExpenseType, TeamUser, UpdateExpenseDto } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/jobs/$id/expenses/$expenseId')({
  component: ExpenseRoutePage,
});

const EXPENSE_TYPES: { value: ExpenseType; label: string }[] = [
  { value: 'MATERIALS', label: 'Materials' },
  { value: 'EQUIPMENT_RENTAL', label: 'Equipment Rental' },
  { value: 'SUBCONTRACTOR', label: 'Subcontractor' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'PERMIT', label: 'Permit' },
  { value: 'DISPOSAL', label: 'Disposal' },
  { value: 'OTHER', label: 'Other' },
];

const REIMBURSE_NONE = '__none__';

interface ExpenseFormState {
  appointmentId: string;
  expenseType: ExpenseType;
  name: string;
  description: string;
  quantity: string;
  unitCost: string;
  vendor: string;
  date: string;
  reimburseToUserId: string;
}

function dateToLocalInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function localInputToDate(value: string): Date | undefined {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function fmtAppointment(startAt?: string | null, timeZone?: string | null): string {
  if (!startAt) return 'Needs scheduling';
  try {
    return formatDateTimeInTimeZone(startAt, timeZone, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return startAt;
  }
}

function userName(user: TeamUser): string {
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return (user.displayName ?? fullName) || user.email;
}

const EXPENSE_TYPE_LABELS = EXPENSE_TYPES.reduce(
  (acc, item) => ({ ...acc, [item.value]: item.label }),
  {} as Record<ExpenseType, string>,
);

function formatStatusLabel(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatDate(value?: string | null, timeZone?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return formatDateTimeInTimeZone(value, timeZone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-foreground">{value}</dd>
    </div>
  );
}

function serializeForm(form: ExpenseFormState): string {
  return JSON.stringify(form);
}

function emptyForm(): ExpenseFormState {
  return {
    appointmentId: '',
    expenseType: 'MATERIALS',
    name: '',
    description: '',
    quantity: '1',
    unitCost: '0',
    vendor: '',
    date: dateToLocalInput(new Date()),
    reimburseToUserId: '',
  };
}

function formFromExpense(expense: Expense): ExpenseFormState {
  return {
    appointmentId: expense.appointmentId ?? '',
    expenseType: expense.expenseType,
    name: expense.name || expense.description || '',
    description: expense.description ?? '',
    quantity: String(expense.quantity),
    unitCost: String(expense.unitCost),
    vendor: expense.vendor ?? '',
    date: expense.date.slice(0, 10),
    reimburseToUserId: expense.reimburseToUserId ?? '',
  };
}

function ExpenseRoutePage() {
  const { id, expenseId } = Route.useParams();
  const router = useRouter();
  const { data: expense, isLoading, isError } = useExpense(id, expenseId);
  const jobFinancialsRoute = {
    to: '/dashboard/jobs/$id',
    params: { id },
    search: { tab: 'finances' as const },
  };

  if (isLoading) {
    return (
      <FormPageLayout
        title="Expense details"
        onBack={() => void router.navigate(jobFinancialsRoute)}
        noscroll
      >
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading expense...
        </div>
      </FormPageLayout>
    );
  }

  if (isError || !expense) {
    return (
      <FormPageLayout
        title="Expense details"
        onBack={() => void router.navigate(jobFinancialsRoute)}
        noscroll
      >
        <div className="mx-auto max-w-lg px-4 py-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            Expense not found.
          </div>
        </div>
      </FormPageLayout>
    );
  }

  if (expense.status === 'DRAFT') {
    return <ExpenseFormPage jobId={id} expenseId={expenseId} />;
  }

  return <ExpenseDetailPage jobId={id} expenseId={expenseId} />;
}

function ExpenseDetailPage({ jobId, expenseId }: { jobId: string; expenseId: string }) {
  // Resolved PER APPOINTMENT: a job can hold visits in different site
  // zones, so one page-wide zone misprints the out-of-zone ones.
  const { zoneFor } = useScheduleTimezone();
  const router = useRouter();
  const { data: job, isLoading: isJobLoading } = useJob(jobId);
  const { data: expense, isLoading: isExpenseLoading } = useExpense(jobId, expenseId);
  const { data: appointments, isLoading: appointmentsLoading } = useJobAppointments(jobId);
  const { data: usersData } = useUsers();
  const { data: profitability } = useJobProfitability(jobId);
  const companyTimeZone = useCompanyTimezone();
  const approveExpense = useApproveExpense(jobId);
  const rejectExpense = useRejectExpense(jobId);
  const approveReimbursement = useApproveReimbursement(jobId);
  const payReimbursement = usePayReimbursement(jobId);

  const navigateBack = () =>
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id: jobId },
      search: { tab: 'finances' as const },
    });

  if (isJobLoading || isExpenseLoading || appointmentsLoading) {
    return (
      <FormPageLayout title="Expense details" onBack={navigateBack} noscroll>
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading expense...
        </div>
      </FormPageLayout>
    );
  }

  if (!expense) {
    return (
      <FormPageLayout title="Expense details" onBack={navigateBack} noscroll>
        <div className="mx-auto max-w-lg px-4 py-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            Expense not found.
          </div>
        </div>
      </FormPageLayout>
    );
  }

  const users = usersData?.data ?? [];
  const userById = new Map(users.map((user) => [user.id, userName(user)]));
  const appointment = (appointments ?? []).find((item) => item.id === expense.appointmentId);
  const appointmentLabel = appointment
    ? `${fmtAppointment(appointment.startAt, zoneFor(appointment.siteTimezone))} · ${appointment.serviceType?.name ?? 'Appointment'}`
    : 'Appointment';
  const isPending = expense.status === 'PENDING';
  const isApproved = expense.status === 'APPROVED';
  const canApproveReimbursement =
    isApproved && Boolean(expense.reimburseToUserId) && expense.reimbursementStatus === 'PENDING';
  const canPayReimbursement =
    isApproved && Boolean(expense.reimburseToUserId) && expense.reimbursementStatus === 'APPROVED';
  const currentCosts = profitability?.expenses ?? 0;
  const projectedCosts =
    expense.status === 'APPROVED' || expense.status === 'REJECTED'
      ? currentCosts
      : currentCosts + expense.total;
  const revenue =
    profitability?.invoiced && profitability.invoiced > 0
      ? profitability.invoiced
      : (job?.grandTotal ?? 0);
  const newMargin = revenue > 0 ? ((revenue - projectedCosts) / revenue) * 100 : 0;
  const subtitle = [
    job?.jobNumber,
    job?.jobType,
    formatStatusLabel(expense.status),
    formatCurrency(expense.total),
  ]
    .filter(Boolean)
    .join(' · ');
  const actionPending =
    approveExpense.isPending ||
    rejectExpense.isPending ||
    approveReimbursement.isPending ||
    payReimbursement.isPending;

  return (
    <FormPageLayout
      title={`Expense · ${expense.name}`}
      subtitle={subtitle}
      onBack={navigateBack}
      noscroll
      actions={
        <>
          {isPending ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                disabled={actionPending}
                onClick={() =>
                  rejectExpense.mutate({
                    id: expense.id,
                    reason: 'Rejected from detail view',
                  })
                }
              >
                Reject
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={actionPending}
                onClick={() => approveExpense.mutate(expense.id)}
              >
                Approve
              </Button>
            </>
          ) : null}
          {canApproveReimbursement ? (
            <Button
              type="button"
              size="sm"
              disabled={actionPending}
              onClick={() => approveReimbursement.mutate(expense.id)}
            >
              Approve reimbursement
            </Button>
          ) : null}
          {canPayReimbursement ? (
            <Button
              type="button"
              size="sm"
              disabled={actionPending}
              onClick={() => payReimbursement.mutate(expense.id)}
            >
              Mark reimbursement as paid
            </Button>
          ) : null}
        </>
      }
    >
      <div className="h-full overflow-y-auto">
        <div className="mx-auto grid max-w-[1120px] gap-4 px-4 py-4 sm:px-6 lg:grid-cols-[minmax(0,760px)_300px]">
          <div className="space-y-4">
            <SectionCard>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusBadge
                      tone={expenseStatusTone(expense.status)}
                      label={formatStatusLabel(expense.status)}
                    />
                    <span className="text-sm text-muted-foreground">
                      Created {formatDate(expense.createdAt, companyTimeZone)}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Name
                    </p>
                    <p className="mt-1 text-base font-medium text-foreground">{expense.name}</p>
                  </div>
                  {expense.description ? (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Description
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">{expense.description}</p>
                    </div>
                  ) : null}
                </div>
                <div className="rounded-lg bg-muted/70 px-3 py-2 text-sm">
                  <p className="text-muted-foreground">Total</p>
                  <p className="font-semibold text-foreground">{formatCurrency(expense.total)}</p>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Expense details">
              <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                <DetailRow
                  label="Type"
                  value={EXPENSE_TYPE_LABELS[expense.expenseType] ?? expense.expenseType}
                />
                <DetailRow label="Date" value={formatDate(expense.date)} />
                <DetailRow label="Appointment" value={appointmentLabel} />
                <DetailRow label="Vendor" value={expense.vendor || '-'} />
                <DetailRow label="Quantity" value={String(expense.quantity)} />
                <DetailRow label="Unit cost" value={formatCurrency(expense.unitCost)} />
              </dl>
            </SectionCard>

            {expense.reimburseToUserId ? (
              <SectionCard title="Reimbursement">
                <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                  <DetailRow
                    label="Reimburse to"
                    value={userById.get(expense.reimburseToUserId) ?? 'Selected user'}
                  />
                  <DetailRow
                    label="Reimbursement status"
                    value={
                      <StatusBadge
                        tone={reimbursementStatusTone(expense.reimbursementStatus)}
                        label={formatStatusLabel(expense.reimbursementStatus)}
                      />
                    }
                  />
                  {expense.reimbursementPaidAt ? (
                    <DetailRow
                      label="Paid at"
                      value={formatDate(expense.reimbursementPaidAt, companyTimeZone)}
                    />
                  ) : null}
                </dl>
              </SectionCard>
            ) : null}

            {(expense.attachments?.length ?? 0) > 0 ? (
              <SectionCard title="Receipts">
                <div className="mt-4">
                  <ExpenseAttachmentsSection expenseId={expense.id} readOnly />
                </div>
              </SectionCard>
            ) : null}

            <SectionCard title="History">
              <ExpenseHistory expense={expense} userById={userById} timeZone={companyTimeZone} />
            </SectionCard>
          </div>

          <aside className="lg:sticky lg:top-4 lg:self-start">
            <section className="rounded-lg bg-muted/70 p-4 shadow-xs">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Financial impact
              </h2>

              <div className="mt-5 space-y-4 text-sm">
                <ImpactRow label="Amount" value={expense.total} tone="neutral" total />
                <ImpactRow
                  label="Reimburse"
                  value={expense.reimburseToUserId ? expense.total : 0}
                  tone={expense.reimburseToUserId ? 'warning' : 'neutral'}
                  placeholder={expense.reimburseToUserId ? undefined : '-'}
                  total={Boolean(expense.reimburseToUserId)}
                />
                <div className="border-t pt-4">
                  <ImpactRow label="Net cost to company" value={expense.total} prominent total />
                </div>
              </div>

              <h3 className="mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Impact on job profitability
              </h3>
              <div className="mt-3 space-y-3 text-sm">
                <ImpactRow label="Current job costs" value={currentCosts} tone="neutral" total />
                <ImpactRow
                  label="New job costs"
                  value={projectedCosts}
                  tone={projectedCosts > currentCosts ? 'danger' : 'neutral'}
                  total
                />
                <div className="border-t pt-3">
                  <ImpactRow
                    label="New margin"
                    value={newMargin}
                    tone={newMargin > 0 ? 'success' : 'danger'}
                    isPercent
                  />
                </div>
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                Current revenue: {formatCurrency(revenue)}
              </p>
            </section>
          </aside>
        </div>
      </div>
    </FormPageLayout>
  );
}

function ExpenseHistory({
  expense,
  userById,
  timeZone,
}: {
  expense: Expense;
  userById: Map<string, string>;
  timeZone?: string | null;
}) {
  const createdBy = expense.createdBy ? (userById.get(expense.createdBy) ?? 'Unknown user') : null;
  const approvedBy = expense.approvedBy
    ? (userById.get(expense.approvedBy) ?? 'Unknown user')
    : null;

  return (
    <div className="mt-4 space-y-3 text-sm">
      <HistoryRow
        label="Created"
        value={`${formatDate(expense.createdAt, timeZone)}${createdBy ? ` by ${createdBy}` : ''}`}
      />
      {expense.status === 'PENDING' ? (
        <HistoryRow label="Submitted" value={formatDate(expense.updatedAt, timeZone)} />
      ) : null}
      {expense.status === 'APPROVED' ? (
        <HistoryRow
          label="Approved"
          value={`${formatDate(expense.approvedAt, timeZone)}${approvedBy ? ` by ${approvedBy}` : ''}`}
        />
      ) : null}
      {expense.status === 'REJECTED' ? (
        <HistoryRow
          label="Rejected"
          value={`${formatDate(expense.updatedAt, timeZone)}${
            expense.rejectionReason ? ` - ${expense.rejectionReason}` : ''
          }`}
        />
      ) : null}
      {expense.status === 'VOIDED' ? (
        <HistoryRow label="Voided" value={formatDate(expense.updatedAt, timeZone)} />
      ) : null}
      {expense.reimbursementStatus === 'APPROVED' ? (
        <HistoryRow label="Reimbursement approved" value="Awaiting payment" />
      ) : null}
      {expense.reimbursementStatus === 'PAID' ? (
        <HistoryRow
          label="Reimbursement paid"
          value={formatDate(expense.reimbursementPaidAt, timeZone)}
        />
      ) : null}
    </div>
  );
}

function HistoryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
      <p className="font-medium text-foreground">{label}</p>
      <p className="text-sm text-muted-foreground">{value}</p>
    </div>
  );
}

function ExpenseFormPage({ jobId, expenseId }: { jobId: string; expenseId: string }) {
  // Resolved PER APPOINTMENT: a job can hold visits in different site
  // zones, so one page-wide zone misprints the out-of-zone ones.
  const { zoneFor } = useScheduleTimezone();
  const id = jobId;
  const router = useRouter();
  const allowNextNavigationRef = useRef(false);
  const initializedExpenseIdRef = useRef<string | null>(null);
  const initialSnapshotRef = useRef(serializeForm(emptyForm()));
  const { data: job, isLoading: isJobLoading } = useJob(id);
  const { data: expense, isLoading: isExpenseLoading, isError } = useExpense(id, expenseId);
  const { data: appointments, isLoading: appointmentsLoading } = useJobAppointments(id);
  const { data: usersData } = useUsers();
  const { data: profitability } = useJobProfitability(id);
  const update = useUpdateExpense(id);
  const submit = useSubmitExpense(id);

  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [form, setForm] = useState<ExpenseFormState>(() => emptyForm());

  const appointmentOptions = appointments ?? [];
  const users = usersData?.data ?? [];
  const isEditable = expense?.status === 'DRAFT';
  const isDraft = expense?.status === 'DRAFT';
  const qtyValue = Number(form.quantity || '0');
  const costValue = Number(form.unitCost || '0');
  const total = useMemo(() => {
    return Number.isFinite(qtyValue) && Number.isFinite(costValue) ? qtyValue * costValue : 0;
  }, [costValue, qtyValue]);
  const isReimbursable = Boolean(form.reimburseToUserId);
  const currentCosts = profitability?.expenses ?? 0;
  const previousApprovedTotal = expense?.status === 'APPROVED' ? expense.total : 0;
  const newCosts = currentCosts - previousApprovedTotal + total;
  const revenue =
    profitability?.invoiced && profitability.invoiced > 0
      ? profitability.invoiced
      : (job?.grandTotal ?? 0);
  const newMargin = revenue > 0 ? ((revenue - newCosts) / revenue) * 100 : 0;
  const canSave = Boolean(isEditable && form.appointmentId && form.name.trim());
  const canSubmit =
    canSave &&
    Number.isFinite(qtyValue) &&
    qtyValue > 0 &&
    Number.isFinite(costValue) &&
    costValue > 0 &&
    Boolean(form.date);
  const isSaving = update.isPending || submit.isPending;
  const isDirty = serializeForm(form) !== initialSnapshotRef.current;
  const hasUnsavedWork = Boolean(isEditable && isDirty && !isSaving);
  const jobFinancialsRoute = {
    to: '/dashboard/jobs/$id',
    params: { id },
    search: { tab: 'finances' as const },
  };
  const subtitle = job
    ? `${job.jobNumber} · ${job.jobType.name} · current total ${formatCurrency(job.grandTotal ?? 0)}`
    : undefined;

  useEffect(() => {
    if (!expense) return;
    if (initializedExpenseIdRef.current === expense.id) return;
    const next = formFromExpense(expense);
    setForm(next);
    initialSnapshotRef.current = serializeForm(next);
    initializedExpenseIdRef.current = expense.id;
  }, [expense]);

  const blocker = useBlocker({
    disabled: !hasUnsavedWork,
    enableBeforeUnload: false,
    withResolver: true,
    shouldBlockFn: () => {
      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false;
        return false;
      }
      return true;
    },
  });

  function updateField<K extends keyof ExpenseFormState>(key: K, value: ExpenseFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function leaveWithoutPrompt() {
    allowNextNavigationRef.current = true;
    void router.navigate(jobFinancialsRoute);
  }

  function handleExit() {
    if (hasUnsavedWork) {
      setExitConfirmOpen(true);
      return;
    }
    leaveWithoutPrompt();
  }

  function closeExitConfirm() {
    setExitConfirmOpen(false);
    if (blocker.status === 'blocked') blocker.reset();
  }

  function confirmExit() {
    setExitConfirmOpen(false);
    if (blocker.status === 'blocked') {
      blocker.proceed();
      return;
    }
    leaveWithoutPrompt();
  }

  function buildPayload(): UpdateExpenseDto {
    return {
      appointmentId: form.appointmentId,
      expenseType: form.expenseType,
      name: form.name.trim(),
      description: form.description.trim() || null,
      quantity: Number(form.quantity || '0'),
      unitCost: Number(form.unitCost || '0'),
      vendor: form.vendor.trim() || null,
      date: form.date,
      reimburseToUserId: form.reimburseToUserId || null,
    };
  }

  async function save() {
    if (!expense || !isEditable) return void toast.error('This expense cannot be edited.');
    if (!canSave) return void toast.error('Select an appointment and add a name.');
    await update.mutateAsync({ id: expense.id, dto: buildPayload() });
    leaveWithoutPrompt();
  }

  async function submitForApproval() {
    if (!expense || !isDraft) return;
    if (!canSubmit)
      return void toast.error('Quantity, unit cost, and date are required to submit.');

    if (isDirty) {
      await update.mutateAsync({ id: expense.id, dto: buildPayload() });
    }
    await submit.mutateAsync(expense.id);
    leaveWithoutPrompt();
  }

  const isLoading = isJobLoading || isExpenseLoading || appointmentsLoading;

  if (isLoading) {
    return (
      <FormPageLayout
        title="Edit expense"
        onBack={() => void router.navigate(jobFinancialsRoute)}
        noscroll
      >
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading expense...
        </div>
      </FormPageLayout>
    );
  }

  if (isError || !expense) {
    return (
      <FormPageLayout
        title="Expense details"
        onBack={() => void router.navigate(jobFinancialsRoute)}
        noscroll
      >
        <div className="mx-auto max-w-lg px-4 py-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            Expense not found.
          </div>
        </div>
      </FormPageLayout>
    );
  }

  return (
    <>
      <FormPageLayout
        title={isEditable ? 'Edit expense' : 'Expense details'}
        subtitle={subtitle}
        onBack={handleExit}
        noscroll
        actions={
          <>
            <Button type="button" variant="outline" size="sm" onClick={handleExit}>
              Cancel
            </Button>
            {isEditable ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canSave || !isDirty || isSaving}
                onClick={() => void save()}
              >
                {update.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Save
              </Button>
            ) : null}
            {isDraft ? (
              <Button
                type="button"
                size="sm"
                disabled={!canSubmit || isSaving}
                onClick={() => void submitForApproval()}
              >
                {(update.isPending || submit.isPending) && (
                  <Loader2 className="size-3.5 animate-spin" />
                )}
                Send for approval
              </Button>
            ) : null}
          </>
        }
      >
        <div className="h-full overflow-y-auto">
          <div className="mx-auto grid max-w-[1120px] gap-4 px-4 py-4 sm:px-6 lg:grid-cols-[minmax(0,760px)_300px]">
            <div className="space-y-4">
              <SectionCard className="border-l-4 border-l-foreground/70">
                <div className="space-y-3">
                  <Label className="text-xs font-semibold uppercase tracking-wide">
                    Expense details <span className="text-destructive">· Required</span>
                  </Label>
                  {!isEditable ? (
                    <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                      {expense.status.toLowerCase()} expenses are locked after review.
                    </div>
                  ) : null}

                  <div className="space-y-1.5">
                    <Label htmlFor="appointment">Appointment</Label>
                    <Select
                      value={form.appointmentId}
                      onValueChange={(value) => updateField('appointmentId', value)}
                      disabled={!isEditable}
                    >
                      <SelectTrigger id="appointment" className="w-full">
                        <SelectValue placeholder="Select appointment" />
                      </SelectTrigger>
                      <SelectContent>
                        {appointmentOptions.map((appointment) => (
                          <SelectItem key={appointment.id} value={appointment.id}>
                            {fmtAppointment(appointment.startAt, zoneFor(appointment.siteTimezone))}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
                    <div className="space-y-1.5">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(event) => updateField('name', event.target.value)}
                        placeholder="e.g. Replacement valve"
                        disabled={!isEditable}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Type</Label>
                      <Select
                        value={form.expenseType}
                        onValueChange={(value) => updateField('expenseType', value as ExpenseType)}
                        disabled={!isEditable}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EXPENSE_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="description">
                      Description <span className="text-muted-foreground">(optional)</span>
                    </Label>
                    <Textarea
                      id="description"
                      value={form.description}
                      onChange={(event) => updateField('description', event.target.value)}
                      placeholder="Add any details — vendor, reason, PO number, who paid, etc."
                      rows={3}
                      disabled={!isEditable}
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="quantity">Qty</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={form.quantity}
                        onChange={(event) => updateField('quantity', event.target.value)}
                        disabled={!isEditable}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="unit-cost">Unit cost</Label>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          $
                        </span>
                        <Input
                          id="unit-cost"
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={form.unitCost}
                          onChange={(event) => updateField('unitCost', event.target.value)}
                          className="pl-7"
                          disabled={!isEditable}
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Date</Label>
                      <DatePicker
                        value={localInputToDate(form.date)}
                        onChange={(date) => date && updateField('date', dateToLocalInput(date))}
                        placeholder="Expense date"
                        disabled={!isEditable}
                      />
                    </div>
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Reimbursement">
                <div className="mt-4 space-y-3">
                  <div className="space-y-1.5">
                    <Label>Reimburse to</Label>
                    <Select
                      value={form.reimburseToUserId || REIMBURSE_NONE}
                      onValueChange={(value) =>
                        updateField('reimburseToUserId', value === REIMBURSE_NONE ? '' : value)
                      }
                      disabled={!isEditable}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={REIMBURSE_NONE}>Not reimbursable</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {userName(user)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Select the user who paid out of pocket. Leave as "Not reimbursable" if paid with
                    company card.
                  </p>
                </div>
              </SectionCard>

              <SectionCard title="Receipts & vendor">
                <div className="mt-4 space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="vendor">Vendor</Label>
                    <Input
                      id="vendor"
                      value={form.vendor}
                      onChange={(event) => updateField('vendor', event.target.value)}
                      placeholder="Vendor or supplier"
                      disabled={!isEditable}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Receipts</Label>
                    <ExpenseAttachmentsSection expenseId={expense.id} readOnly={!isEditable} />
                    <p className="text-xs text-muted-foreground">
                      Take a photo or upload images / PDFs (max 5MB each).
                    </p>
                  </div>
                </div>
              </SectionCard>
            </div>

            <aside className="lg:sticky lg:top-4 lg:self-start">
              <section className="rounded-lg bg-muted/70 p-4 shadow-xs">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Financial impact
                </h2>

                <div className="mt-5 space-y-4 text-sm">
                  <ImpactRow label="Amount" value={total} tone="neutral" total />
                  <ImpactRow
                    label="Reimburse"
                    value={isReimbursable ? total : 0}
                    tone={isReimbursable ? 'warning' : 'neutral'}
                    placeholder={isReimbursable ? undefined : '-'}
                    total={isReimbursable}
                  />
                  <div className="border-t pt-4">
                    <ImpactRow label="Net cost to company" value={total} prominent total />
                  </div>
                </div>

                <h3 className="mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Impact on job profitability
                </h3>
                <div className="mt-3 space-y-3 text-sm">
                  <ImpactRow label="Current job costs" value={currentCosts} tone="neutral" total />
                  <ImpactRow
                    label="New job costs"
                    value={newCosts}
                    tone={newCosts > currentCosts ? 'danger' : 'neutral'}
                    total
                  />
                  <div className="border-t pt-3">
                    <ImpactRow
                      label="New margin"
                      value={newMargin}
                      tone={newMargin > 0 ? 'success' : 'danger'}
                      isPercent
                    />
                  </div>
                </div>
                <p className="mt-4 text-xs text-muted-foreground">
                  Current revenue: {formatCurrency(revenue)}
                </p>
              </section>
            </aside>
          </div>
        </div>
      </FormPageLayout>

      <AlertDialog
        open={exitConfirmOpen || blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open) closeExitConfirm();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes in this expense. Leaving now will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeExitConfirm}>Stay</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={(event) => {
                event.preventDefault();
                confirmExit();
              }}
            >
              Leave without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
